import { targetConfig } from '@smartproxy/sp-utils';
import { useEffect, useState } from 'react';

import { UseFormReturn } from '@/hooks/useForm';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { requestLanguages } from '@/decodo/blocks/Scraper/constants/languages';
import { responseFormatViews } from '@/decodo/blocks/Scraper/constants/response-formats';
import { CodeBlockItem } from '@/decodo/components/atoms/CodeBlock/CodeBlockItem';
import { Tabs } from '@/decodo/components/atoms/Tabs';
import { HTMLResponseView } from './HTMLResponseViewer';

import { EmptyRichResponse } from './components/EmptyRichResponse';
import { RequestResponseFooter } from './components/RequestResponseFooter';
import { ResponseStatus } from './components/ResponseStatus';
import { buildRequestCode } from './helpers';
import { JSONResponseView } from './JSONResponseView';
import { useScraper } from './state/context';

type RequestResponseProps = {
  form: UseFormReturn<Record<string, string>>;
  response: unknown;
  error: unknown;
};

export const RequestResponse = ({
  form,
  response,
  error,
}: RequestResponseProps) => {
  const { target } = useScraper();

  const { texts } = useTranslation();
  const [requestLanguageIndex, setRequestLanguageIndex] = useState(0);
  const [responseFormatIndex, setResponseFormatIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'request' | 'response'>('request');
  const [showingToast, setShowingToast] = useState(true);

  useEffect(() => {
    if (!response) return;

    setActiveTab('response');
    setShowingToast(true);
    const tId = setTimeout(() => {
      setShowingToast(false);
    }, 5000);

    return () => {
      clearTimeout(tId);
    };
  }, [response]);

  useEffect(() => {
    if (!error) return;

    setShowingToast(true);
    const tId = setTimeout(() => {
      setShowingToast(false);
    }, 5000);

    return () => {
      clearTimeout(tId);
    };
  }, [error]);

  const activeRequestLanguage = requestLanguages[requestLanguageIndex];

  const requestCode = buildRequestCode({
    formValues: form.values,
    language: activeRequestLanguage,
    target: target,
  });

  const responseFormat = targetConfig[target.name].format;
  const selectedResponseFormatView = responseFormatViews[responseFormatIndex];
  const hasStatus = !!error || !!response;
  const isHTMLPreview =
    responseFormat === 'html' && selectedResponseFormatView === 'rich';

  const getStringifiedCopyPayload = (): string => {
    const contentToCopy = activeTab === 'request' ? requestCode : response;

    if (!contentToCopy) return '';

    if (typeof contentToCopy === 'object') {
      try {
        return JSON.stringify(contentToCopy, null, 2);
      } catch (error) {
        return String(contentToCopy);
      }
    }

    return String(contentToCopy);
  };

  return (
    <div className='bg-base-black flex h-full flex-col overflow-hidden rounded dark:border dark:border-gray-300 dark:bg-gray-50'>
      <div
        className={cn('relative flex-grow overflow-hidden p-6 pb-0')}
        style={{
          // ensures scrollbars match the dark theme on safari
          colorScheme: 'dark',
        }}
      >
        {hasStatus && (
          <div className='absolute left-0 top-16 z-10 h-6 w-full overflow-hidden'>
            <ResponseStatus
              isVisible={showingToast}
              status={!!error ? 'error' : 'success'}
            />
          </div>
        )}
        <div className='bg-base-black h-full dark:bg-gray-50'>
          <Tabs
            value={activeTab}
            label='Code Block'
            classNames={{
              root: 'bg-base-black dark:bg-gray-50',
              outerContentContainer: 'dark',
              tabContent: isHTMLPreview ? 'p-0' : '',
            }}
            tabs={[
              {
                value: 'request',
                label: texts.scraper.tabs.request,
                content: (
                  <div className='dark h-full overflow-y-auto pb-6'>
                    <CodeBlockItem
                      ContainerProps={{
                        className: 'bg-transparent',
                      }}
                      code={requestCode}
                      language={activeRequestLanguage}
                    />
                  </div>
                ),
              },
              {
                value: 'response',
                label: texts.scraper.tabs.response,
                content: response ? (
                  <div
                    className={cn(
                      'h-full overflow-y-auto pb-6',
                      isHTMLPreview && 'pb-0'
                    )}
                  >
                    {responseFormat === 'json' ? (
                      <JSONResponseView
                        viewType={selectedResponseFormatView}
                        data={(response as any)?.results?.[0]?.content}
                      />
                    ) : (
                      <HTMLResponseView
                        viewType={selectedResponseFormatView}
                        data={response}
                      />
                    )}
                  </div>
                ) : error ? (
                  <JSONResponseView viewType='text' data={error} />
                ) : (
                  <EmptyRichResponse
                    message={texts.scraper.no_response_message}
                  />
                ),
              },
            ]}
            onValueChange={setActiveTab}
          />
        </div>
      </div>
      <RequestResponseFooter
        activeTab={activeTab}
        copyText={getStringifiedCopyPayload()}
        hasResponse={!!response}
        requestLanguageIndex={requestLanguageIndex}
        responseFormatIndex={responseFormatIndex}
        setRequestLanguageIndex={setRequestLanguageIndex}
        setResponseFormatIndex={setResponseFormatIndex}
      />
    </div>
  );
};
